import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Storage } from '@angular/fire/storage';
import { CoordinatorService } from 'coordinator';

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private readonly storage: Storage = inject(Storage);

    constructor(public coords: CoordinatorService) {}

    /**
     * Currently, only English language is supported.
     **/
    async initializeTranslations(http: HttpClient): Promise<void> {
        const lang = 'en';

        try {
            const translationFileUrl =
                'https://firebasestorage.googleapis.com/v0/b/memowl.appspot.com/o/translations%2Fen.txt?alt=media&token=632cc3bc-0c76-4f9f-8fb5-7c6388207345';
            const data = await fetch(translationFileUrl);
            console.log(`Translation data for language ${lang} fetched successfully, applying... `);
            this.setTranslationsMap(await data.text());
        } catch (err) {
            console.error('Error fetching translation data, will use fallback translations: ', err);
            this.setTranslationsMap();
        }
    }

    setTranslationsMap(txt?: string | null) {
        const ls: [string, string][] = [];

        if (!txt) {
            console.warn('No translation data provided, using fallback/default data');
            txt = this.fallbackTranslationData;
        }

        try {
            txt.split('\n').forEach((x: string) => {
                const i = x.indexOf(':');
                if (i != -1 && !x.trim().startsWith('#'))
                    ls.push([x.slice(0, i).trim().toLowerCase(), x.slice(i + 1).trim()]);
            });

            const translationMap = new Map(ls);
            this.coords.display$.textMap$.next(translationMap);
        } catch (err) {
            console.error('Error parsing translation data: ', err);
        }
    }

    private get fallbackTranslationData() {
        return `
       about: about
       Abstract: Abstract
       abstract_desc: Search only in abstract. Use standard operators: 'and', 'or', 'not'.
       AcceptCode: Accept code
       AcceptingInvitation: Accepting invitation...
       account_settings: Settings
       active_members: active members
       active_project: active project
       add_abstracts: Add Abstracts
       add_attachment_upper: ADD ATTACHMENT
       add_group_upper: ADD GROUP
       add_identificator: ADD IDENTIFICATOR
       add_keywords_upper: ADD KEYWORDS
       add_member: add member(s)
       add_to_project: add to project
       add_to_subsets: Add to subset(s)
       addField: Add field
       AddItem: Add a reference
       all: All
       allProjects: All projects
       and: and
       app_version: App version
       Applicant: Applicant
       applicant_desc: Applicants' name (in case of patents).
       applicantLs: Applicant(s)
       applicants: Applicant(s)
       applicationDate: Application date
       applicationIdentifier: Application identifier
       applicationNumber: Application number
       article: article
       articleNumber: Article number
       artwork: artwork
       arxiv: ArXiv
       assigneeLs: Asignee(s)
       attachFile: Attach file
       attachment_cache: Attachment cache
       attachments: Attachments
       Attachments: Attachments
       author/inventor: Author/Inventor
       author/inventor-first: Author/Inventor (first)
       author/inventor-first_desc: Names of authors listed as first in the article.
       author/inventor-last: Author/Inventor (last)
       author/inventor-last_desc: Names of authors listed as last in the article.
       author/inventorLs: Author/Inventor
       author/inventorLs_desc: Authors' or inventors' (in case of patents) names.
       author: Author
       author-first: Author (first)
       author-first_desc: Names of authors listed as first in the article.
       author-last: Author (last)
       author-last_desc: Names of authors listed as last in the article.
       authorLs: Author(s)  
       authorLs_desc: Authors' or inventors' (in case of patents) names.
       authors-title-startPage-year: Title/Authors/Year/Page
       authors-title-startPage-year-desc: Identifying duplicates based on title, authors, publication year and and start page of the article.
       AutousersImport: Autousers' import
       autouserTransfer: Autouser transfer
       available: Available
       back:back
       back_to_group_upper: BACK TO GROUP
       back_to_List: Back to List
       BACK_TO_LIST: BACK TO LISTS
       BackupLocal: Backup local
       batch: Batch
       Biblio: Biblio Info
       book: book
       book-chapter: book chapter
       bookSeries: Series
       Bulk: Bulk
       BulkOperations: Bulk operations
       can_join: Outsiders can join via
       canAttach: allowed to add attachments
       cancel: Cancel
       catalog: catalog
       Citation: Citation
       citation_style: citation style
       citations: Citations
       classic: classic
       Clear: Clear
       CLEAR_ALL: Clear all
       ClearAttachmentCache: Clear attachment cache
       ClearCache: Clear cache
       ClearClipboard: Clear Clipboard
       ClearCloudCache: Clear cloud cache (recommended)
       ClearLocalCache: Clear local cache
       client: Client
       clientContact: Contact person  
       Clipboard: Clipboard
       cloud_project_cache: Cloud project cache
       CloudError: Cloud Error
       CloudProject: Cloud project
       cloudProjectLoading: Cloud project loading...
       Code: Code
       code_created: Code created
       collection: Collection
       Collection: Collection
       collection_and_subsets: Collection and Subsets
       collection-preview: Collection is empty. 
       collectionWasChanged: Collection was changed
       comments: Comments
       conference: conference
       conference-paper: conference paper
       confLocation: Conference location
       confTitle: Conference title
       confYear: Conference year
       Connections: Resource(s) 
       contacts: Contacts
       contacts:contacts
       Continue: Continue
       convert_to_cloud: To enable file upload, convert to â€œcloud projectâ€
       copy_code: copy code
       copy_link: copy link
       COPY_PROJECT_LINK: COPY PROJECT LINK
       copyDataToCurrentUser: Copy data to current user
       copyLink: Copy Link
       correction: correction
       cpc: CPC
       cpcLs: CPC
       cpcLs_desc: Cooperative Patent Classification codes.
       create_link: Create link
       createCode: Create code
       createConnection: Create resource
       createGroup: Create group
       Creating_code: Creating code
       crossref: Crossref
       CrossRefSearch: Search
       currentAssign: Current Asignee(s)
       custom_code: custom code
       custom_project_report: Custom Project Export
       custom_setup: Custom setup
       custom_text_report: Custom Text Report
       CustomExpiryDate: Custom expiry date
       cutoff_desc: Fill in a number of maximum requested results.
       czech: Czech
       data_to_export: Data to export
       database: database
       Databins: Databins
       dataset: dataset
       dataset: Dataset
       Dataset: Dataset
       Date: Date
       date_created: Date created
       dbName: Database
       defaultBT-rate-1: Rating-abstract
       defaultBT-rate-2: Rating-fulltext
       defaultBT-tag-1: Importance tags
       deleteCode: Delete code
       delete-from-main: Delete from Collection (it remains in Preview)
       deleteGroup: Delete group
       DeleteLocalProjects: Delete local projects
       DeleteProject: Delete project
       deliveryDate: Delivery date   
       desc-arxiv: Open-access archive for nearly 2.4 million scholarly articles (physics, mathematics, computer science, statistics, economics etc.), maintained by Cornell University.
       desc-crossref: Almost 50 million Crossref Metadata records for journal articles and conference proceedings, all containing a DOI. Provided by Crossref community.
       desc-pubmed: More than 36 millions of references on life sciences and biomedical topics, maintained by U.S. National Library of Medicine.
       description: description
       description: Description
       descriptionProject: Aims   
       designatedState: Designated states
       dev_tools: Dev Tools
       dictionary: dictionary
       documentType: Document type
       documentTypeLs: Document type
       doi: DOI
       doi_desc: Digital Object Identifier.
       doi-lowercase: DOI
       doi-lowercase-desc: Identifying duplicates based on article's DOI.
       download: Download
       download_text_report: Download text report
       downloadingInvitationGroup: Downloading your previous contacts...
       DownloadReport: Download report
       doYouHaveAccount: Do you have account?
       drag_and_drop: DRAG & DROP HERE
       dragDrop: Drag file here
       Duplicate: Duplicate
       Duplkeys: Duplicates
       earl_priority: Earliest priority
       earliestPriority: Earliest priority
       earliestPublication: Earliest publ.
       early_access: early access
       edit: edit
       edit_group_upper: EDIT GROUP
       editorial_material: editorial
       editorLs: Editor(s)
       editResource: Edit resource
       eissnLs: E-ISSN
       Email: E-mail
       email_id:email or id
       empty_message_team: One does not simply make a team
       empty_warning: This group is empty
       empty-active-item: No reference selected. 
       empty-collection-instruction: Please, select a reference in Preview to add in Collection.
       empty-preview: Preview is empty.
       empty-preview-instruction: Please, add a reference resource in Databins to preview them.
       encyclopedia: encyclopedia
       endPage: End page
       english: English
       enter_your_code: Enter your code
       equation: equation
       err_CorrectName: A project with this name already exists. Please choose a different name.
       error_PubmedLimitExceeded: Limit of downloadable results exceeded
       excludeFilter: excl.
       expirDate: Anticipated expiration date
       expiry_in: Expiry in
       ExpiryDate: Expiry date
       explainDeduplication: (number of results includes potential identical references which will be automatically removed)
       Export: Export
       export_full_project: Export project
       export_project: PROJECT BACKUP
       export_project_noContent: There is no content to be exported.
       export-CSV: CSV format
       export-CSV-description: Export selected results into CSV format
       export-DOIs: DOI list
       export-DOIs-description: Export of all DOIs found in selected results
       exported_collection: Exported collection will contain
       ExportingProject: Exporting project...
       export-JSON-everything: Full export (JSON)
       export-JSON-everything-description: Export selected results together with team work
       export-JSON-identifiers: Export identifiers (JSON)
       export-JSON-identifiers-description: Extract all IDs, DOIs and URLs from selected results, their duplicates and their counterparts in Preview/Collection, if exist
       export-Patentka: Internal format
       export-Patentka-description: Export selected results into app's own format
       export-RIS: RIS format
       export-RIS-description: Export selected results into RIS format
       export-XLS: XLS format
       export-XLS-description: Export selected results into XLS format
       External: External Search
       FailedFetch: Failed fetch
       familyNumber: Family number
       FamilySearch: Family Search
       Fetch_data: FETCH DATA
       field-abstract: Abstract
       field-applicant: Applicant
       field-author-first: Author (first)
       field-author-last: Author (last)
       field-authorLs: Author/Inventor
       field-cutoff: Maximum hit count
       field-doi: DOI
       field-id: ID
       field-langLs: Language
       field-source: Source
       field-text: Title/Abstract
       field-title: Title
       field-year: Year
       figure: figure
       Files: Files
       Filesystem: File
       filingDate: Filing date   
       filingIdentifier: Filing identifier
       film: film
       filter-author: Author
       filter-author-first: Author (first)
       filter-author-last: Author (last)
       filter-doctype: Document type  
       filter-has: Has
       filter-keyword: Keyword
       filter-language: Language
       filters: Filters
       filter-source: Source
       filter-year:Year
       firstPage: First page
       font: Font
       General: General
       generate_code: generate code
       generate_link: Generate Link
       generic: generic
       GlobalLog: Global log
       grant: grant
       group: Group
       group_name: Group name
       hasAttachment: has attachment
       hasDoi: has DOI
       hasDuplicate: has duplicate
       here: here
       hiddenPanels: hidden panels
       hiddenPanels: panel(s) hidden
       hide: Hide
       HideAll: Hide all
       hint_acceptCode: join a project by filling in a code acquired from owner of the project
       hint_addAttachment: add attachment
       hint_addNewSubset: add new subset
       hint_arxiv: Search in all fields for maximum of 2000 references in one instance.
       hint_backToList: return to the List mode
       hint_bookmark: number of bookmarked references
       hint_bulk: bulk operations on active records
       hint_clip: paste your references from a clipboard
       hint_collection: number of references in Collection
       hint_comment: add a comment to the reference
       hint_crossref:  Search for a reference either by its DOI(s) or by title, authors etc. (up to 10 most relevant records).
       hint_deleteD: delete this databin and its content, it will no longer be displayed in Preview
       hint_deleteR: delete this source, it will no longer be displayed in Preview
       hint_detailDelete: delete this reference from Collection (it remains in Preview)
       hint_detailDisplay: display settings
       hint_duplD: make a copy of this databin and its content
       hint_editBib: edit details of this reference
       hint_editR:  edit the source
       hint_editRatings: add or edit ratings
       hint_editTags: add or edit tags
       hint_export: save selected references to your disk
       hint_exportD: save the databin to your disk
       hint_exportDs: save all the databins to your disk
       hint_files: add references by uploading your own file
       hint_filter: number of selected (filtered/found) references
       hint_first_item: go to first reference of this selected list
       hint_groupWeight: keyword present in more groups will be highlighted according to a group with higher weight 
       hint_hideUser: hide user's work in this project
       hint_hitset: Save this query for future use in your project. Results are saved as a hitset.
       hint_hitsetSearch: combine and search saved hitset
       hint_includeR: include/exclude references from this source in Preview
       hint_includeSubset: display subset in the project
       hint_limitation: timespan/geographic/language
       hint_load_search: upload previously saved search query
       hint_loadD: Load databin previously exported from the app
       hint_loadP: Load project previously exported from the app
       hint_loadSearch: perform again your previously saved search
       hint_manageAttach: permission to add, edit or delete attachments in the project
       hint_next: go to the next reference of this selected list
       hint_next_unread: go to the next reference you have not read/opened yet
       hint_NotDuplicate: not duplicate, create separate reference
       hint_notes: personal notes to the project 
       hint_oldResource: The search has expired, results cannot be downloaded. Perform new search.
       hint_online: add references by searching in online sources
       hint_pin: add this field to result list
       hint_prev: go to previous reference of this selected list
       hint_preview: number of references in Preview
       hint_previewD: see this databin in Preview
       hint_previewR: see this source in Preview
       hint_ratingSearch: search for specifically rated references
       hint_readMode: enter the read mode
       hint_rejected: number of rejected references
       hint_reportReferences: number of references in each dataset selected for the export
       hint_reportSubsetName: check if you want to have the subset structure (name) in the exported Collection
       hint_resetSeen: reset number of references you have read/opened to 1
       hint_resultBook: bookmark this reference
       hint_resultColl: add to Collection
       hint_resultDupl: found duplicates of this reference
       hint_resultHitset: save the active result list for further searches
       hint_resultNotDupl: click here if the reference is not a duplicate and make it a standard reference
       hint_resultReject: reject this reference
       hint_resultSeen: mark as seen
       hint_searchAgain: search again
       hint_seen: number of seen references
       hint_SingleItemMode: open the Read mode
       hint_subsetSearch: combine and search selected subsets
       hint_tagSearch: search for specific combination of tags
       hint_total: total number of references
       Hits: Hits
       howToStart: How to start
       check-biblio: Check biblio
       check-DOI: Check DOI
       check-fulltext: Check fulltext 
       check-orcid: Check ORCID
       choose_cit_style: Choose citation style
       id: ID
       id_desc: Identificator of the original source.
       id-lowercase: ID
       id-lowercase-desc: In case of identical ID, only one reference is displayed in Preview. 
       importDataFromOthers: Import data from others
       inCloud: Cloud project
       include_databins: Include databins
       include_subset_name: Include subset name
       includeFilter: -
       info_Arxiv: Due to unreliability of ArXiv service the number of downloaded results can differ.
       info_FileTypesHelp: Supported file formats are txt, ris, csv. For details see
       info_PubmedLimit: PubMed allows to download max. 9999 results.
       in-press-article: in-press article
       inPreview: in Preview
       inProgressLink: Generating a link...
       instruction_local_to_cloud: Changing the project to a cloud is irreversible action.
       interview: interview
       inventor-first: Inventor (first)
       inventor-last: Inventor (last)
       inventorLs: Inventor(s)
       invert: Invert
       invertSelection: Invert selection
       invintation_code_: invitation code.
       invitation_to_be: Invitation to be a
       invitations: invitations
       INVITATIONSTOPROJECT: INVITATIONS TO PROJECTS
       invite:invite
       invite_codes: invite codes
       invite_in_progress: Invitation is being sent, please wait
       InviteGroup: Invite group
       ipc: IPC
       ipcLs: IPC
       ipcLs_desc: International Patent Classification codes.
       isAlreadyMember: The user is already member of this group.
       isBackground: is background
       isbnLS: ISBN
       IsCloudProject: Cloud Project
       isCurrent-member: Existing member of the team cannot be invited
       isCurrent-pending: Sending invitation...
       isCurrentUser: The user already participates in the project.
       isOpposing: is opposing
       isProjectBook: Project Book
       isPublicIdentifier: Public identificator
       issnLs: ISSN
       issue: Issue
       isSupportive: is supportive
       join_code_does_not_exist:This code doesnâ€™t exist or has expired
       JoinProject: Join a project
       JoinProjectCode: Fill in the code
       journal: journal
       keyword: Keyword
       keyword_desc: Filter according to your own keywords.
       keywordGroupEditor: Keyword group editor
       Keywords: Keywords
       keyWordsProject: Keywords 
       KW-highlight: Keywords highlighting 
       Lang: Language
       lang_desc: Language of the references.
       langLS: Language
       langLs_desc: Language (mostly 3 character code, eg. eng, chi, ger, fre).
       languages: Languages
       lastCloudSaveNotCompleted: Last save of the cloud project has not been completed properly
       lastPage: Last page
       lastVisitedDate: Last visited date
       late_priority: Latest priority
       latestProjects: Latest projects
       LeaveProject: Leave project
       legal: legal
       legal-bill: legal bill
       legal-case: legal case
       legalStatus: Legal status
       limitation: Limitations   
       link: Link
       link_created: Link successfully created
       loadDatabin: Load databin
       loading_projects: Loading projects
       LoadProject: Load project
       loadprojectnew: Load project
       loadSearchProcedure: Load search procedure
       local: Local
       local_project: Local project
       LocalCloudConflict: Local cloud conflict
       localToCloudProject: Change local project to cloud
       LOG_IN: LOG IN
       LOG_OUT: LOGOUT
       LoginProcessingInProgress: Login processing in progress.. 
       logout: Logout
       magazine: magazine
       main: collection
       manage_attachments: Manage attachments
       manual: manual
       manuscript: manuscript
       map: map
       margin: Margin
       meeting_ab: Meeting abstract
       Member: Member
       MemowlStart: Welcome to MemOwl
       MemowlTabTitle: MemOwl
       Misc: Other
       misc_desc: Other filters, e.g. references having a DOI or a duplicate.
       mode-viewer: Save to Projects
       moreFilters: More
       moreSearchFields: More
       msg_clipboardCleared: Clipboard cleared.
       msg_clipboardWithoutParsableData: Clipboard is either empty or records are in unrecognizable format.
       msg_empty-active-item: No reference selected.
       msg_empty-collection: Collection is empty.
       msg_empty-collection-instruction: To add a reference to the Collection select it in 
       msg_empty-databin: Add references by searching ONLINE, uploading a FILE or parsing the CLIPBOARD content.
       msg_empty-preview: Preview is empty.
       msg_empty-preview-instruction: To see references here add their resource in 
       msg_faultClip: Clipboard is either empty or records are in unrecognizable format.
       msg_faultFile: Cannot read the file, it is either empty or in unrecognizable format.
       msg_FetchInProgress: Fetching references in progress
       msg_importingFiles: Importing files...
       msg_isCurrent-member: Existing member of the team cannot be invited
       msg_noHits: No hits!
       msg_no-hits: No results found!
       msg_onlyLoggedUsersCanUseCloud: Cloud project is available for logged users only.
       msg_parsing: Parsing file: 
       msg_searchStarted: Searching...
       msg_SearchTermEmpty: Search term is empty
       multimedia: multimedia
       musical-score: musical score
       New: New
       NewConnection: New resource
       NewIdentifier: New identifier
       NewIdentifierAllowedCharacters: Only "a-z" (lowercase), "0-9" and "-_" are allowed.
       newProject: New project
       newSearchField: add search field
       newspaper-article: newspaper article
       no-files-on-server: No files on the server
       none: None
       not: not
       notCloudProject: Team functionality requires cloud project. 
       notDuplicate: Not duplicate
       notes: Notes
       ntk_login_welcomeScreen_left: Do you want to work in cloud? Use your
       ntk_login_welcomeScreen_right: account.
       NTKlogin: EduID login
       NTKreader: NTK client
       ntkResearcher: Researcher  
       one_operator: Only one operator is possible at the time (AND / OR).
       OnlineServices: Online services
       onlyLoggedUsersCanUseCloud: Only logged users can use the cloud
       openingProject: Opening project...
       OpenProjectInProgress: Project opening in progress
       operators_wildcards: Operators/wildcards
       or: or
       orcid: Orcid
       orcidLS: ORCID
       origAssign: Original Asignee(s)
       origTitle: Original title
       other_content: Other content
       overlap_field_missing: Missing
       overlap_field_only: Only
       overlap_field_overlap: Overlap
       overlap_field_size: Size
       overlap_field_uniqueIn-connection: Unique in resources
       overlap_field_uniqueIn-databin: Unique in databins
       overlap_field_uniqueIn-subset: Unique in subsets
       Overlaps: Overlaps
       pageCount: Pages
       pamphlet: pamphlet
       Panels: Display
       ParseClipboard: Parse Clipboard
       patent: patent
       patentNumber: Patent number
       patentStatus: Patent status
       PCTapDate: PCT application date
       PCTapId: PCT application
       PDF: Fulltext
       Permisions: Permisions
       permissions: permissions
       personal: personal
       podcast: podcast
       post-weblog: post weblog
       preparingCloudProject: Launching cloud project...
       press-release: press release
       preview: Preview
       Preview: Preview
       priorityDate: Earliest priority
       priorityDocs: Priorities             
       proceedings: proceedings
       proceedings_paper: proceedings
       Project: Project
       Project_details: Project details
       project_details_client_contact: Client's responsible person
       project_details_delivery_date: Delivery date
       project_details_description: Research goal
       project_details_labels: Project labels
       project_details_limitations: Scope limitations
       project_details_notes: Notes
       project_details_project_client: Client name 
       project_details_project_name: Project name
       project_details_researcher: Researcher
       project_details_start_date: Start date
       project_role: project role
       Project-details: Project Details
       ProjectDetailsReport: Project details
       ProjectList: Project list
       ProjectNew: New project
       projectReport_desc: Export full project or customize the projectâ€™s content according to your needs. Download .REF file or generate a link to open the export as a new project.
       projectReport_download: Download project to share or work locally. Open it as a new local project.
       projectReport_link: Generate link to project export. Open it as a new local project.
       ProjectSearch: Search by project's name or label
       Proxyteam: Proxy team
       Public_visibility: Public visibility
       publicationDate: Publication date
       publicationIdentifier: Publication identifier
       publicationNumber: Publication number
       Published: Published
       publisher: Publisher
       publisherCity: Publisher location
       pubmed: Pubmed
       queries: Queries
       query: query
       rater: contributor
       rating_groups: Rating groups
       Ratings: Ratings
       RatingSearch: Rating search
       reading_settings: Reading Settings
       receiveNotifications: Receive notifications
       ReferenceReport: References
       References: References
       ReferentkaStart: Welcome to RefereNTKa
       ReferentkaTabTitle: RefereNTKa
       RefreshClipboard: Refresh clipboard
       regular_text: This is the regular text
       report: report
       Report: Report
       reportAsProject: Report as project
       ReportPreview: Text report
       requireFilter: reqr.
       requires_log_in: Team functionality requires log-in.
       resources: Resources
       resultsDisplayed: results displayed
       resultsFound: results found
       retrievedDate: Retrieved date
       review: review
       Role: Role
       save: save
       save_file: save file
       save_url: save url
       saveAsScript: save as script
       SavedHitsets: Saved hitsets
       savedSearches: Hitsets
       SaveHitset: Save hitset
       search: Search
       Search_ratings: Rating search
       searchArchive: Search archive
       search-fields: Search fields
       search-filters: Filters
       searchSavedHitsetsOrAndAlternative: Choose combination of hitsets to be searched:
       searchSubsets: Subset search
       SearchUser: Search user
       searchViewOrAndAlternative: Choose combination of subsets to be searched:
       Select: Select
       select_all: Select All
       Select_field: Select field
       SelectFiles: Select file
       selection: Selection
       selectionViewOrAndAlternative: Choose combination to be searched:
       SelectVisiblePanels: Display settings
       serial: serial
       ServerUpload: Server Upload
       set_cloud_project: Set the cloud project
       share_link: Share link
       show: Show
       show_all_upper: SHOW ALL
       ShowAll: Show all
       sign_register: Sign-in or Register
       SITEM: READ
       size_subset: Size
       size-includingDuplicates: Size including duplicates
       slide: slide
       software: software
       Sort: Sort
       SortByFirstAuthor: By first author
       sortByImportOrder: By import order
       SortByLastAuthor: By last author
       sortByTitle: By title
       sortByWeight: By weight
       sortByYear: By year
       sortRandom: Randomly
       sound: sound
       Source: Source
       source_desc: Journals' full titles. 
       sourceAbr: Journal abr.
       source-volume-year-startPage: Source/Vol/Year/Page
       source-volume-year-startPage-desc: Identifying duplicates based on journal's name, volume, publication year and start page of the article.
       standard: standard
       startDate: Start date
       startPage: Start page    
       statue: statue
       STORAGE: Storage
       storage_space: STORAGE SPACE
       storage_type: storage type
       SubmitAnyway: Submit anyway
       sub-rating: SUB-RATING
       subset: Subset
       subset-fromMain: Delete from Collection
       subset-rejected: Mark as Rejected
       Subsets: Subsets
       SubsetSearch: Subset search
       subset-seen: Mark as Seen
       subset-to-main: Add to Collection
       subset-toMain: Add to Collection
       Summary: Summary
       SynchronizingCloudData: Synchronizing cloud data
       System: System status
       tab_explain_custom-project_collection-subsets: Choose which dataset should be included in Collection of the exported project and if subsets should be indicated.
       tab_explain_custom-project_keywords: Select keywords which should be highlighted in the exported project.
       tab_explain_custom-project_other-content: Choose other features which should be transferred to the new project.
       tab_explain_custom-project_project-databins: Choose whether databins should be present in the exported project.
       tab_explain_custom-project_project-details: Setup detail of the exported project.
       tab_explain_custom-project_rating-groups: Choose which rating should be transferred to the new project.
       tab_explain_custom-project_tag-groups: Choose which tag group should be transferred to the new project.
       tab_explain_custom-report_citations: Choose your favourite citation style and decide whether you need abstracts in the report. 
       tab_explain_custom-report_collection-subsets: Choose which references should be included in your report.
       tab_explain_custom-report_languages: Choose language of the report. When both languages are chosen, two documents are downloaded. 
       tab_explain_custom-report_other-content: Choose other details of the report.
       tab_explain_custom-report_project-details: Setup header details of your report and then download it as a Word document.
       tab_explain_project_attachments: Add file or URL attachment for the whole project.
       tab_explain_project_duplkeys: Set which reference fields will be taken into account while identifying of duplicate references in your Preview. 
       tab_explain_project_general: Fill in all information about the project and decide if the project is stored on a cloud or locally in your device. 
       tab_explain_project_keywords: Create, import and edit keywords and its groups applicable for the whole project.
       tab_explain_project_proxyteam: Your team full of robots which finally take over your project!!!
       tab_explain_project_ratings: Create and edit ratings applicable for the whole project.
       tab_explain_project_tags: Create and edit tag groups applicable for the whole project.
       tab_explain_project_team: Invite other people to work on the project with you.
       tab_explain_project_views: Manage subsets in your Collection for better organization of your references.
       tab_explain_report_collection: Decide what should be in the Collection of the report.
       tab_explain_report_keywords: Decide which keywords should be indicated in the report.
       tab_explain_report_misc: Decide which part of the project should be visible in the report.
       tab_explain_report_project: Create a header for your report, keep the info from this project or change it.
       tab_explain_report_subsets: Decide which subsets should be indicated in the report.
       tab_explain_summary_keywords: Overview of keywords retrieved in references in both Preview and Collection. 
       tab_explain_summary_overlaps: Identify references which are included in more than one databin, resource or subset within this project.
       tab_explain_summary_ratings: Overview of ratings assigned to references in Collection.
       tab_explain_summary_tags: Overview of tags assigned to references in Collection.
       tab_explain_summary_views: Number of references included in Collection subsets.
       tag_groups: Tag groups
       tag_name: tag name
       TagisNotAvailable: This identificator already exists. Please choose a different one.
       tagLS: Project labels
       Tags: Tags
       team: team
       team_members: team members
       team_tags: Team tags
       teamRating: Team rating
       testing: testing
       textReport_desc: Create a text report with project details and list of citations from Collection or subset selected in custom setup. 
       thesis: thesis
       this_user_already_is_in_list: This user is already a team member or has been invited.
       title: Title
       title_desc: Search in documents' titles only.
       title+abstract: Title/Abstract
       title-abstract_desc: Search in title or abstract. Use standard operators: 'and', 'or', 'not'.
       titleProject: Title
       title-startPage: Title/Page
       title-startPage-desc: Identifying duplicates based on title and start page of the article.
       Transform: Change
       tryGooglePatents: Google Patents
       unableToRetrieve: Unable to check validity of this URL:
       UnknownUser: Unknown user
       unpublished-work: unpublished work
       UPDATEGROUP: Update group
       UpdateKeywordGroup: Update
       upload_file: upload file
       upload_from_computer: upload from computer
       upload_query: Upload query
       uploadingLocalDataToCloud: Uploading local data to cloud
       uploadingProject: Uploading project
       url: URL
       url_attachments: URL attachments
       url-lowercase: URL
       url-lowercase-desc: Identifying duplicates based on article's URL.
       user(s):user(s)
       user: user
       user_name: user name
       user_not_found: User not found.
       UserDetail: User details
       UserGroup: User group
       UserGroupInvitation: User group invitation
       UserGroupList: User group list
       UserGroups: User groups
       UserIdentificators: User identificators
       UserIdentified: User identified as
       UserIdentifier: User identifier
       userNotFound: User not found.
       UsersImport: Users' import
       values: Values
       ValuesSearch: Value search
       version: VERSION
       video: video
       view: subset
       View: View
       volume: Volume
       webpage: webpage
       weight: Weight
       WHERETOSTART: Choose where to start
       wrongFileFormat: Wrong file format (only .patentka files can be uploaded).
       Year: Year
       year_desc: Publishing year (e.g. '2015', '< 2020', '> 2014' or '>2014 and <2020').
       dev_tools: Dev Tools
       hint_selectionMember: filter will be applied only on selected member's tags/ratings 
       export-TEAM-RT: Team work (CSV)
       export-TEAM-RT-description: Export ID, DOI, citations and team's ratings and tags
       NO_TAG: NO TAG
       NO_RATING: NO RATING
       ANY_TAG: ANY TAG
       ANY_RATING: ANY RATING
       Search_user: Search user
       ADVANCED_FILTERS: Advanced filters
       QUICK_FILTERS: Quick filters
       hint_includeTag: Hide tags
       new_field: New field
       abstract: abstract
`;
    }
}
